import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../api';
import ImageLightbox from '../components/ImageLightbox';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function Catalog() {
  const { category: categorySlug } = useParams();
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [pdfPlayerOpen, setPdfPlayerOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfBlobLoading, setPdfBlobLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [pdfPageIndex, setPdfPageIndex] = useState(0);
  const [pdfDisplayedPanel, setPdfDisplayedPanel] = useState(0); // 0 or 1, which panel is visible
  const [pdfPendingPanel, setPdfPendingPanel] = useState(null); // when set, flip to this panel on that panel's onRenderSuccess
  const [pdfHiddenPanelOverride, setPdfHiddenPanelOverride] = useState(null); // when set (prev), hidden panel shows this 1-based page

  useEffect(() => {
    api.get('/api/categories')
      .then(res => setCategories(res.data))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setCurrentCategory(null);
    if (categorySlug) {
      api.get(`/api/categories/slug/${categorySlug}`)
        .then(res => setCurrentCategory(res.data))
        .catch(() => setCurrentCategory(null))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [categorySlug]);

  const apiBase = process.env.REACT_APP_API_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const pdfUrl = currentCategory?.catalog_pdf
    ? (currentCategory.catalog_pdf.startsWith('http') ? currentCategory.catalog_pdf : `${apiBase}${currentCategory.catalog_pdf}`)
    : null;

  const onPdfLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
    setPdfLoadError(null);
  };
  const onPdfLoadError = () => {
    setPdfLoadError(true);
    setNumPages(null);
  };

  // When opening the modal, fetch PDF via API (same base URL / proxy as rest of app)
  useEffect(() => {
    if (!pdfPlayerOpen || !currentCategory?.id) {
      setPdfBlob(null);
      setPdfLoadError(null);
      setNumPages(null);
      setPdfPageIndex(0);
      setPdfDisplayedPanel(0);
      setPdfPendingPanel(null);
      setPdfHiddenPanelOverride(null);
      return;
    }
    if (!currentCategory.catalog_pdf) {
      setPdfLoadError(true);
      setPdfBlobLoading(false);
      return;
    }
    setPdfLoadError(null);
    setNumPages(null);
    setPdfBlobLoading(true);
    setPdfBlob(null);
    api.get(`/api/categories/${currentCategory.id}/catalog-pdf`, { responseType: 'blob' })
      .then(res => {
        const contentType = (res.headers['content-type'] || '').toLowerCase();
        if (res.status !== 200 || !contentType.includes('pdf')) {
          setPdfLoadError(true);
          return;
        }
        const data = res.data;
        if (data && data instanceof Blob && data.size > 0) {
          setPdfBlob(new Blob([data], { type: 'application/pdf' }));
        } else {
          setPdfLoadError(true);
        }
      })
      .catch(() => setPdfLoadError(true))
      .finally(() => setPdfBlobLoading(false));
  }, [pdfPlayerOpen, currentCategory?.id, currentCategory?.catalog_pdf]);

  const images = currentCategory?.images || [];
  const imageUrls = images.map(url => url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || ''}${url}`);

  if (loading) {
    return <div className="loading">A carregar...</div>;
  }

  if (!categorySlug) {
    return (
      <div className="container" style={{ paddingTop: '48px', paddingBottom: '72px' }}>
        <h1 style={{ marginBottom: '12px', fontSize: '2.2rem' }}>Coleções</h1>
        <hr style={{ width: '50px', height: '2px', background: 'var(--accent)', border: 'none', marginBottom: '32px' }} />
        <p style={{ color: 'var(--text-secondary)', marginBottom: '36px', fontWeight: 300 }}>
          Escolha uma categoria para ver imagens e catálogo em PDF.
        </p>
        <div className="category-list">
          {categories.map(cat => (
            <Link
              key={cat.id}
              to={`/catalog/${cat.slug}`}
              className="category-btn"
            >
              {cat.name}
            </Link>
          ))}
        </div>
        {categories.length === 0 && (
          <p style={{ color: 'var(--text-secondary)' }}>Nenhuma categoria disponível.</p>
        )}
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <h1>Categoria não encontrada</h1>
        <Link to="/catalog" className="btn btn-primary" style={{ marginTop: '24px' }}>Voltar às coleções</Link>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 0 }}>
      <div className="container" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <Link to="/catalog" style={{ color: 'var(--text-secondary)', marginBottom: '24px', display: 'inline-block', fontSize: '0.88rem', letterSpacing: '0.03em' }}>
          ← Voltar às coleções
        </Link>
        <h1 style={{ marginBottom: '12px', fontSize: '2.2rem' }}>{currentCategory.name}</h1>
        <hr style={{ width: '50px', height: '2px', background: 'var(--accent)', border: 'none', marginBottom: '28px' }} />
        {currentCategory.description && (
          <p style={{ color: 'var(--text-secondary)', marginBottom: '40px', maxWidth: '680px', fontWeight: 300, lineHeight: '1.75', fontStyle: 'italic', fontFamily: 'var(--font-heading)' }}>
            {currentCategory.description}
          </p>
        )}

        {images.length > 0 && (
          <>
            <div className="category-images-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '20px',
              marginBottom: '52px'
            }}>
              {images.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  style={{
                    aspectRatio: '1',
                    overflow: 'hidden',
                    borderRadius: 'var(--radius, 8px)',
                    background: 'var(--background-secondary)',
                    border: '1px solid var(--border, rgba(255,255,255,0.06))',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'block',
                    width: '100%',
                    transition: 'border-color 0.3s ease, transform 0.3s ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.transform = ''; }}
                >
                  <img
                    src={url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || ''}${url}`}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', transition: 'transform 0.5s ease' }}
                  />
                </button>
              ))}
            </div>
            {lightboxIndex !== null && (
              <ImageLightbox
                images={imageUrls}
                currentIndex={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onNavigate={(delta) => setLightboxIndex(prev => prev + delta)}
              />
            )}
          </>
        )}

        {images.length === 0 && !pdfUrl && (
          <p style={{ color: 'var(--text-secondary)' }}>Nenhuma imagem nem PDF definidos para esta categoria.</p>
        )}
      </div>

      {pdfUrl && (
        <section className="category-pdf-section" style={{ width: '100%', padding: '24px 20px 40px' }}>
          <div className="container" style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => setPdfPlayerOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px 32px',
                background: 'var(--background-secondary)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius-lg, 14px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                color: 'var(--text)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-subtle, rgba(201,160,80,0.08))'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(201,160,80,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--background-secondary)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)'; }}
            >
              <span style={{ color: 'var(--accent)', fontSize: '0.88rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Ver catálogo →</span>
            </button>
          </div>
        </section>
      )}

      {pdfUrl && pdfPlayerOpen && (
        <div
          className="modal-overlay"
          style={{ zIndex: 2000 }}
          onClick={() => setPdfPlayerOpen(false)}
        >
          <div
            style={{
              width: 'min(920px, 95vw)',
              maxHeight: '95vh',
              overflow: 'auto',
              background: 'var(--background)',
              borderRadius: '12px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              padding: '24px 24px 20px',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPdfPlayerOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                zIndex: 10,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                fontSize: '20px',
                cursor: 'pointer',
                lineHeight: 1
              }}
              aria-label="Fechar"
            >
              ×
            </button>
            {!pdfBlobLoading && !pdfLoadError && pdfBlob && numPages != null && numPages > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '12px',
                paddingRight: '48px',
                flexWrap: 'wrap'
              }}>
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  disabled={pdfPageIndex <= 0 || pdfPendingPanel != null}
                  onClick={() => {
                    if (pdfPageIndex <= 0) return;
                    const prevPageNum = pdfPageIndex; // 1-based prev page
                    setPdfHiddenPanelOverride(prevPageNum);
                    setPdfPendingPanel(1 - pdfDisplayedPanel);
                  }}
                >
                  ← Anterior
                </button>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                  Página {pdfPageIndex + 1} de {numPages}
                </span>
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  disabled={pdfPageIndex >= numPages - 1 || pdfPendingPanel != null}
                  onClick={() => {
                    if (pdfPageIndex >= numPages - 1) return;
                    setPdfPageIndex(i => i + 1);
                    setPdfDisplayedPanel(p => 1 - p);
                  }}
                >
                  Seguinte →
                </button>
              </div>
            )}
            <div
              style={{
                marginBottom: '16px',
                background: 'var(--background)',
                borderRadius: '8px',
                overflow: 'auto',
                maxHeight: '75vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '8px 0'
              }}
            >
              {pdfBlobLoading ? (
                <div style={{ padding: '40px', color: 'var(--text-secondary)' }}>A carregar PDF…</div>
              ) : pdfLoadError ? (
                <p style={{ padding: '20px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                  Não foi possível carregar o PDF.{' '}
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
                    Abrir numa nova janela
                  </a>
                </p>
              ) : pdfBlob ? (
                <Document
                  file={pdfBlob}
                  onLoadSuccess={onPdfLoadSuccess}
                  onLoadError={onPdfLoadError}
                >
                  {(() => {
                    const currentPageNum = pdfPageIndex + 1;
                    const pageWidth = Math.min(560, (typeof window !== 'undefined' ? window.innerWidth : 920) * 0.85);
                    const hiddenPageNum = pdfHiddenPanelOverride ?? Math.min(currentPageNum + 1, numPages);
                    const panel0PageNum = pdfDisplayedPanel === 0 ? currentPageNum : hiddenPageNum;
                    const panel1PageNum = pdfDisplayedPanel === 1 ? currentPageNum : hiddenPageNum;
                    const commitPendingFlip = () => {
                      if (pdfPendingPanel == null) return;
                      setPdfDisplayedPanel(pdfPendingPanel);
                      if (pdfHiddenPanelOverride != null) setPdfPageIndex(pdfHiddenPanelOverride - 1);
                      setPdfPendingPanel(null);
                      setPdfHiddenPanelOverride(null);
                    };
                    return (
                      <div style={{ position: 'relative', width: '100%', minHeight: '400px' }}>
                        <div
                          style={{
                            position: pdfDisplayedPanel === 0 ? 'relative' : 'absolute',
                            visibility: pdfDisplayedPanel === 0 ? 'visible' : 'hidden',
                            left: 0,
                            top: 0,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <Page
                            pageNumber={panel0PageNum}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onRenderSuccess={pdfPendingPanel === 0 ? commitPendingFlip : undefined}
                          />
                        </div>
                        <div
                          style={{
                            position: pdfDisplayedPanel === 1 ? 'relative' : 'absolute',
                            visibility: pdfDisplayedPanel === 1 ? 'visible' : 'hidden',
                            left: 0,
                            top: 0,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <Page
                            pageNumber={panel1PageNum}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onRenderSuccess={pdfPendingPanel === 1 ? commitPendingFlip : undefined}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </Document>
              ) : null}
            </div>
            <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontSize: '0.95rem' }}>
                Abrir numa nova janela
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Catalog;
